import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Email, Lock, ArrowBack, Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { useActionLock } from '../hooks/useActionLock';

const ForgotPassword = () => {
  const navigate = useNavigate();
  // Steps: 1 = Enter Email, 2 = Enter OTP, 3 = Reset Password, 4 = Success
  const [step, setStep] = useState(1);
  const [submitLocked, runSubmit] = useActionLock();
  const [resendLocked, runResend] = useActionLock();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef([]);

  const [formData, setFormData] = useState({
    email: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleOtpChange = (index, value) => {
    if (error) setError('');
    if (success) setSuccess('');

    const numericValue = value.replace(/\D/g, '');

    if (numericValue.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = numericValue;
      setOtp(newOtp);

      if (numericValue && index < 5) {
        otpRefs.current[index + 1]?.focus();
      }
    } else if (numericValue.length === 6) {
      const newOtp = numericValue.split('');
      setOtp(newOtp);
      otpRefs.current[5]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedData[i] || '';
      }
      setOtp(newOtp);
      const lastFilledIndex = Math.min(pastedData.length - 1, 5);
      otpRefs.current[lastFilledIndex]?.focus();
    }
  };

  // Step 1: Initiate forgot password and send OTP
  const handleInitiate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.email) {
      setError('Please enter your email address.');
      return;
    }

    try {
      // Step 1: Initiate
      const initiateResult = await authService.forgotPasswordInitiate(formData.email);

      if (!initiateResult.success) {
        setError(initiateResult.error || 'Failed to initiate password reset.');
        return;
      }

      const initiateToken = initiateResult.data.session_token;

      // Step 2: Send OTP via email automatically
      const sendOtpResult = await authService.forgotPasswordSendOtp(initiateToken, 'email');

      if (sendOtpResult.success) {
        // Use session_token from send-otp response for verify-otp
        const otpToken = sendOtpResult.data.session_token || initiateToken;
        setSessionToken(otpToken);
        setSuccess('OTP has been sent to your email address.');
        setStep(2);
      } else {
        setError(sendOtpResult.error || 'Failed to send OTP.');
      }
    } catch (err) {
      console.error('Initiate error:', err);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const otpValue = otp.join('');

    if (otpValue.length !== 6) {
      setError('Please enter a valid 6-digit OTP.');
      return;
    }

    try {
      const result = await authService.forgotPasswordVerifyOtp(sessionToken, otpValue);

      if (result.success) {
        // Use session_token from verify-otp response for reset
        if (result.data.session_token) {
          setSessionToken(result.data.session_token);
        }
        setSuccess('OTP verified successfully.');
        setStep(3);
      } else {
        setError(result.error || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.newPassword) {
      setError('Please enter a new password.');
      return;
    }

    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      const result = await authService.forgotPasswordReset(
        sessionToken,
        formData.newPassword,
        formData.confirmPassword
      );

      if (result.success) {
        setStep(4); // Success step
      } else {
        setError(result.error || 'Failed to reset password.');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setError('');
    setSuccess('');

    try {
      const result = await authService.forgotPasswordSendOtp(sessionToken, 'email');

      if (result.success) {
        // Update session_token from send-otp response if provided
        if (result.data.session_token) {
          setSessionToken(result.data.session_token);
        }
        setSuccess('OTP has been resent to your email.');
        setOtp(['', '', '', '', '', '']);
      } else {
        setError(result.error || 'Failed to resend OTP.');
      }
    } catch (err) {
      console.error('Resend OTP error:', err);
      setError('An unexpected error occurred.');
    }
  };

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: '#F8FAFC',
      '& fieldset': {
        borderColor: '#D1D9E0',
      },
      '&:hover fieldset': {
        borderColor: '#4E83BD',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4E83BD',
        borderWidth: '2px',
      },
    },
    '& .MuiInputBase-input': {
      fontSize: '14px',
      color: '#2C3E50',
      padding: '14px',
    },
  };

  const getStepTitle = () => {
    switch (step) {
      case 1: return 'Forgot Password';
      case 2: return 'Verify OTP';
      case 3: return 'Reset Password';
      case 4: return 'Password Reset';
      default: return 'Forgot Password';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 1: return 'Enter your email to receive OTP';
      case 2: return `Enter the 6-digit code sent to ${formData.email}`;
      case 3: return 'Create your new password';
      case 4: return 'Your password has been reset successfully';
      default: return '';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
        padding: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 450,
          width: '100%',
          padding: 4,
          borderRadius: 3,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
          position: 'relative',
        }}
      >
        {/* Back Button for Steps 2 and 3 */}
        {(step === 2 || step === 3) && (
          <IconButton
            onClick={() => {
              if (step === 2) {
                setStep(1);
                setOtp(['', '', '', '', '', '']);
              } else if (step === 3) {
                setStep(2);
              }
              setError('');
              setSuccess('');
            }}
            sx={{
              position: 'absolute',
              left: 16,
              top: 16,
              color: '#6B7280',
            }}
          >
            <ArrowBack />
          </IconButton>
        )}

        {/* Logo/Title Section */}
        <Box sx={{ textAlign: 'center', marginBottom: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#2C3E50',
              marginBottom: 1,
              fontSize: '28px',
            }}
          >
            Meri Zimmedari
          </Typography>
        </Box>

        {/* Header Text */}
        <Box sx={{ marginBottom: 3, textAlign: 'center' }}>
          {step === 4 ? (
            <CheckCircle sx={{ fontSize: 60, color: '#10B981', marginBottom: 2 }} />
          ) : null}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#2C3E50',
              marginBottom: 0.5,
              fontSize: '24px',
            }}
          >
            {getStepTitle()}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#6B7280',
              fontSize: '14px',
            }}
          >
            {getStepDescription()}
          </Typography>
        </Box>

        {/* Success Alert */}
        {success && step !== 4 && (
          <Alert severity="success" sx={{ marginBottom: 2.5 }}>
            {success}
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ marginBottom: 2.5 }}>
            {error}
          </Alert>
        )}

        {/* Step 1: Email Form */}
        {step === 1 && (
          <form onSubmit={(e) => { e.preventDefault(); runSubmit(() => handleInitiate(e)); }}>
            <Box sx={{ marginBottom: 2.5 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2C3E50',
                  marginBottom: 1,
                }}
              >
                Email Address
              </Typography>
              <TextField
                fullWidth
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: '#6B7280', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={textFieldStyles}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={submitLocked}
              sx={{
                textTransform: 'none',
                backgroundColor: '#4E83BD',
                color: 'white',
                fontSize: '15px',
                padding: '12px',
                borderRadius: '8px',
                fontWeight: 600,
                marginBottom: 2,
                '&:hover': {
                  backgroundColor: '#3A6A99',
                },
                '&:disabled': {
                  backgroundColor: '#A0B4C7',
                  color: 'white',
                },
              }}
            >
              {submitLocked ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  <span>Sending OTP...</span>
                </Box>
              ) : (
                'Send OTP'
              )}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link to="/signin" style={{ textDecoration: 'none' }}>
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: '#4E83BD',
                    cursor: 'pointer',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <ArrowBack sx={{ fontSize: 16 }} />
                  Back to Sign In
                </Typography>
              </Link>
            </Box>
          </form>
        )}

        {/* Step 2: OTP Verification */}
        {step === 2 && (
          <form onSubmit={(e) => { e.preventDefault(); runSubmit(() => handleVerifyOtp(e)); }}>
            <Box sx={{ marginBottom: 2.5 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2C3E50',
                  marginBottom: 1,
                }}
              >
                Enter OTP
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'space-between',
                }}
              >
                {otp.map((digit, index) => (
                  <TextField
                    key={index}
                    inputRef={(el) => (otpRefs.current[index] = el)}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={handleOtpPaste}
                    inputProps={{
                      maxLength: 1,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      style: {
                        textAlign: 'center',
                        fontSize: '20px',
                        fontWeight: 600,
                        padding: '12px 0',
                      },
                    }}
                    sx={{
                      width: '52px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: '#F8FAFC',
                        '& fieldset': {
                          borderColor: digit ? '#4E83BD' : '#D1D9E0',
                          borderWidth: digit ? '2px' : '1px',
                        },
                        '&:hover fieldset': {
                          borderColor: '#4E83BD',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4E83BD',
                          borderWidth: '2px',
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: '#2C3E50',
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={submitLocked}
              sx={{
                textTransform: 'none',
                backgroundColor: '#4E83BD',
                color: 'white',
                fontSize: '15px',
                padding: '12px',
                borderRadius: '8px',
                fontWeight: 600,
                marginBottom: 2,
                '&:hover': {
                  backgroundColor: '#3A6A99',
                },
                '&:disabled': {
                  backgroundColor: '#A0B4C7',
                  color: 'white',
                },
              }}
            >
              {submitLocked ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  <span>Verifying...</span>
                </Box>
              ) : (
                'Verify OTP'
              )}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: '#6B7280',
                }}
              >
                Didn't receive the code?{' '}
                <Typography
                  component="span"
                  onClick={() => !resendLocked && runResend(handleResendOtp)}
                  sx={{
                    fontSize: '13px',
                    color: resendLocked ? '#94A3B8' : '#4E83BD',
                    cursor: resendLocked ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                    opacity: resendLocked ? 0.8 : 1,
                    '&:hover': resendLocked ? {} : { textDecoration: 'underline' },
                  }}
                >
                  {resendLocked ? 'Sending...' : 'Resend OTP'}
                </Typography>
              </Typography>
            </Box>
          </form>
        )}

        {/* Step 3: Reset Password Form */}
        {step === 3 && (
          <form onSubmit={(e) => { e.preventDefault(); runSubmit(() => handleResetPassword(e)); }}>
            {/* New Password Field */}
            <Box sx={{ marginBottom: 2.5 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2C3E50',
                  marginBottom: 1,
                }}
              >
                New Password
              </Typography>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={formData.newPassword}
                onChange={(e) => handleChange('newPassword', e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: '#6B7280', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#6B7280' }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={textFieldStyles}
              />
            </Box>

            {/* Confirm Password Field */}
            <Box sx={{ marginBottom: 3 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2C3E50',
                  marginBottom: 1,
                }}
              >
                Confirm Password
              </Typography>
              <TextField
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: '#6B7280', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        sx={{ color: '#6B7280' }}
                      >
                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={textFieldStyles}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={submitLocked}
              sx={{
                textTransform: 'none',
                backgroundColor: '#4E83BD',
                color: 'white',
                fontSize: '15px',
                padding: '12px',
                borderRadius: '8px',
                fontWeight: 600,
                marginBottom: 2,
                '&:hover': {
                  backgroundColor: '#3A6A99',
                },
                '&:disabled': {
                  backgroundColor: '#A0B4C7',
                  color: 'white',
                },
              }}
            >
              {submitLocked ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  <span>Resetting Password...</span>
                </Box>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <Box sx={{ textAlign: 'center' }}>
            <Alert severity="success" sx={{ marginBottom: 3 }}>
              Your password has been reset successfully. You can now sign in with your new password.
            </Alert>

            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/signin')}
              sx={{
                textTransform: 'none',
                backgroundColor: '#4E83BD',
                color: 'white',
                fontSize: '15px',
                padding: '12px',
                borderRadius: '8px',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#3A6A99',
                },
              }}
            >
              Go to Sign In
            </Button>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ textAlign: 'center', marginTop: 4, paddingTop: 3, borderTop: '1px solid #ECF0F1' }}>
          <Typography
            variant="body2"
            sx={{
              color: '#6B7280',
              fontSize: '12px',
            }}
          >
            © 2025 Meri Zimmedari. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ForgotPassword;
